import { useState, useEffect, useCallback } from 'react';
import { deliveryZonesApi } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Truck, Plus, Pencil, Trash2, MapPin } from 'lucide-react';
import { DeliveryZoneFormDialog } from './DeliveryZoneFormDialog';

export function DeliveryZonesPanel() {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await deliveryZonesApi.getAll(); setZones(data); }
    catch { toast.error('Failed to load delivery zones'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (payload) => {
    try {
      if (editing) await deliveryZonesApi.update(editing.id, payload); else await deliveryZonesApi.create(payload);
      toast.success(editing ? 'Zone updated' : 'Zone added');
      setDialogOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  const handleDelete = async (z) => {
    if (!window.confirm(`Delete zone "${z.name}"? Customers in these suburbs will get a quote-on-request instead.`)) return;
    try { await deliveryZonesApi.delete(z.id); toast.success('Zone deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  const toggleActive = async (z) => {
    try { await deliveryZonesApi.update(z.id, { ...z, active: !z.active }); load(); }
    catch { toast.error('Update failed'); }
  };

  if (loading) return <div className="animate-pulse h-64 bg-muted rounded-md" data-testid="zones-loading" />;

  return (
    <div className="space-y-4" data-testid="delivery-zones-panel">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-muted-foreground">Customers see the delivery fee instantly when their suburb or postcode matches a zone. Unmatched suburbs get "quote on request".</p>
        <Button className="btn-primary shrink-0" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="add-zone-btn"><Plus className="h-4 w-4 mr-2" /> Add zone</Button>
      </div>

      {zones.length === 0 ? (
        <Card><CardContent className="py-16 text-center text-muted-foreground"><Truck className="h-10 w-10 mx-auto mb-3 text-primary" />No delivery zones yet. Add your first zone to show delivery prices.</CardContent></Card>
      ) : zones.map((z) => (
        <Card key={z.id} className={!z.active ? 'opacity-60' : ''} data-testid={`zone-row-${z.id}`}>
          <CardContent className="p-5 grid lg:grid-cols-[1fr_auto] gap-4">
            <div className="space-y-2 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-heading font-bold text-lg flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> {z.name}</h3>
                <Badge variant="secondary" data-testid={`zone-fee-${z.id}`}>${z.fee.toFixed(2)} delivery</Badge>
                {z.free_over_m2 != null && <Badge variant="outline">Free over {z.free_over_m2} m²</Badge>}
                <button onClick={() => toggleActive(z)} className={`text-xs px-2 py-0.5 rounded-full font-medium ${z.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`} data-testid={`toggle-zone-${z.id}`}>{z.active ? 'Active' : 'Inactive'}</button>
              </div>
              {z.notes && <p className="text-sm text-muted-foreground">{z.notes}</p>}
              <div className="flex flex-wrap gap-1.5">
                {z.suburbs.map((s) => <span key={s} className="text-xs px-2 py-0.5 rounded bg-secondary">{s}</span>)}
                {z.postcodes.map((p) => <span key={p} className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-800 font-mono">{p}</span>)}
              </div>
            </div>
            <div className="flex lg:flex-col gap-2 shrink-0">
              <Button variant="outline" size="sm" onClick={() => { setEditing(z); setDialogOpen(true); }} data-testid={`edit-zone-${z.id}`}><Pencil className="h-4 w-4 mr-1" /> Edit</Button>
              <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={() => handleDelete(z)} data-testid={`delete-zone-${z.id}`}><Trash2 className="h-4 w-4 mr-1" /> Delete</Button>
            </div>
          </CardContent>
        </Card>
      ))}

      <DeliveryZoneFormDialog open={dialogOpen} onOpenChange={setDialogOpen} zone={editing} onSave={handleSave} />
    </div>
  );
}
